import React, { useEffect, useRef, useState } from "react";

import { useTheme } from "../../context/ThemeContext";

import CollapsibleWrapper from "../../components/shared/collapsibleWrapper";
import VICurriculum from "../../components/virtual-internship/viCurriculum";
import { VIHero } from "../../components/virtual-internship/viHero";
import { VIIntro } from "../../components/virtual-internship/viIntro";
import { VIMasterclass } from "../../components/virtual-internship/viMasterclass";
import { VIGlobalteam } from "../../components/virtual-internship/viGlobalteam";
import { VISubjectSlider } from "../../components/virtual-internship/viSubjectSlider";
import { VIStickyBar } from "../../components/virtual-internship/viStickyBar";
import { VIOptionCardList } from "../../components/virtual-internship/viOptionCardList";
import { VIGrant } from "../../components/virtual-internship/viGrant";
import { VIPartnerShip } from "../../components/virtual-internship/viPartnership";
import { VIPrices } from "../../components/virtual-internship/viPrices";
import { VITestimonials } from "../../components/virtual-internship/viTestimonials";
import TestimonialVideoGallery from "../../components/shared/testimonialVideoGallery";
import VIPreviousProjects from "../../components/virtual-internship/viPreviousProjects";
import VITypicalWeek from "../../components/virtual-internship/viTypicalWeek";
import Newsletter from "../../components/newsletter";

const VirtualInternshipPageComponent = ({ data, isProgram = false }) => {
  const sliderRef = useRef(null);
  const stickyBarRef = useRef(null);
  const [currency, setCurrency] = useState("usd");
  const { currentSiteMeta } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      if (stickyBarRef.current) {
        const top = sliderRef.current.getBoundingClientRect().top;
        if (top >= window.innerHeight) {
          stickyBarRef.current.style.bottom = "-168px";
        } else {
          stickyBarRef.current.style.bottom = "0";
        }
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
  }, []);

  useEffect(() => {
    const useCurrency = currentSiteMeta?.currency?.toLowerCase();
    setCurrency(useCurrency);
  }, [currentSiteMeta]);

  const { page, genericDetails, virtualInternshipsTestimonials } = data ?? {};
  const { globalConfig, acfLandingPage } = page ?? {};

  const gviVIConfig = genericDetails?.virtualInternships?.gviVIConfig;
  const overviewContent = isProgram
    ? page?.content
    : gviVIConfig?.viOverviewText;
  const viOverviewTitle = isProgram ? "Overview" : gviVIConfig?.viOverviewTitle;
  const focusAreas = gviVIConfig?.viFocusAreas;
  const viWhatYouGet = gviVIConfig?.viWhatYouGet;
  const viWhatYouGetTitle = gviVIConfig?.viWhatYouGetTitle;
  const bgImage = gviVIConfig?.viSubjectAreaBackgroundImage?.sourceUrl;
  const promoFirstText = gviVIConfig?.viSubjectAreaPromoFirstText;
  const promoMiddleText = gviVIConfig?.viSubjectAreaPromoMiddleText;
  const promoLastText = gviVIConfig?.viSubjectAreaPromoLastText;
  const areaTitle = gviVIConfig?.viSubjectAreasTitle;
  const areaDescription = gviVIConfig?.viSubjectAreasDescription;
  const subCourses = gviVIConfig?.viWhatsIncludedList;
  const courses = gviVIConfig?.viEnrollments ?? [];
  const viTime10 = courses.length > 0 && courses[3] ? courses[3]?.viTime : 0;
  const viTime5 = courses.length > 0 && courses[0] ? courses[0]?.viTime : 0;
  const backgroundImage =
    globalConfig?.backgroundImage ??
    globalConfig?.backgroundImage?.sourceUrl ??
    globalConfig?.backgroundImage?.mediaItemUrl;

  const prgmBackgroundImage =
    acfLandingPage?.backgroundImage ??
    acfLandingPage?.backgroundImage?.sourceUrl ??
    acfLandingPage?.backgroundImage?.mediaItemUrl;

  const viPrgmProjectsTitle =
    gviVIConfig?.viProjectsExamplesTitle ?? "Project work";
  const viPrgmProjectsDesc =
    gviVIConfig?.viProjectsExamplesDescription ??
    "Throughout your internship you’ll be completing one project each week that helps a local organisation solve an actual problem. Since we work with real partners, real, valuable projects will be assigned to you. This means that the types of projects you could work on will vary. We aim to match you as closely as possible with an organisation and a task that aligns with your personal career goals. See below for some of the projects you could work on during your internship.";
  const viPrgmProjects =
    acfLandingPage?.projects?.map((block) => ({
      title: block?.projectName,
      subtitle: "",
      description: block?.projectDescription ?? "",
      url: block?.projectLink ?? false,
      imgUrl: block?.projectImage?.sourceUrl ?? "",
      linkTitle: "",
      contentWrapperClass:
        "flex flex-col p-6 rounded-b bg-white dark:gvi-dark-gray-bg flex-1",
      cardBoxCoverClass: "bg-transparent flex flex-col h-full"
    })) ?? [];

  const viPrgmActivityTitle =
    acfLandingPage?.typicalWeekTitles ?? "A typical week";
  const viPrgmActivities =
    acfLandingPage?.activity?.map((block, idx) => ({
      id: idx,
      title: block?.title ?? "",
      icon: "",
      duration: block?.totalTime ?? "",
      description:
        `${block?.description} ${
          block?.totalTime
            ? `<div class="font-bold dark:font-normal dark:text-white pt-6 w-full flex self-baseline">${block?.totalTime}</div>`
            : ""
        }` ??
        block?.description ??
        "",
      imgUrl: block?.image?.sourceUrl ?? "",
      imgHeight: "h-36",
      descriptionContainerClass: "pt-4 flex-1 flex",
      descriptionClass:
        "text-base text-left text-black-lighter dark:text-gray-generic flex flex-1 justify-between flex-col",
      titleClass: "flex-1 font-light text-black-lighter dark:text-white",
      contentWrapperClass:
        "flex flex-col p-6 rounded-b bg-white dark:gvi-dark-gray-bg flex-1",
      cardBoxCoverClass: "bg-transparent flex flex-col h-full"
    })) ?? [];

  const curriculumTitle =
    gviVIConfig?.viOnlineCoursesTitle ?? "2 online courses included";
  const curriculumDesc = gviVIConfig?.viOnlineCourseDescription ?? "";
  const curriculumExtras = gviVIConfig?.viOptionalExtrasTitle ?? "";
  const curriculumList = gviVIConfig?.viCourses ?? [];
  const curriculumImageDesktop =
    gviVIConfig?.viOnlineCourseImage?.sourceUrl ?? "";
  const curriculumImageMobile =
    gviVIConfig?.viOnlineCourseImageMobile?.sourceUrl ?? "";

  const vi40HoursDurationPrice =
    gviVIConfig?.vi40HoursDurationPrice?.find(
      (price) => price?.vi40Currency === currency
    )?.vi40Amount ?? 0;
  const vi80HoursDurationPrice =
    gviVIConfig?.vi80HoursDurationPrice?.find(
      (price) => price?.vi80Currency === currency
    )?.vi80Amount ?? 0;

  const vi120HoursDurationPrice =
    gviVIConfig?.vi120HoursDurationPrice?.find(
      (price) => price?.vi120Currency === currency
    )?.vi120Amount ?? 0;

  const currencySymbol =
    currentSiteMeta?.currency_symbol ??
    process.env.GATSBY_PROJECT_CURRENCY ??
    "";

  const viDatesAndCostsTitle = gviVIConfig?.viDatesAndCostsTitle;

  const { genFeaturedVideos } = acfLandingPage ?? [];

  const genFeaturedGalleryTitle =
    acfLandingPage?.genFeaturedGalleryTitle ??
    acfLandingPage?.featuredGalleryTitle;

  const viPrgmGallery =
    acfLandingPage?.featuredVideos?.map((video) => ({
      genVideoUrl: video?.videoUrl ?? "",
      genOverlayVideoTitle: video?.videoOverlayTitle ?? ""
    })) ?? [];

  const testimonialCollection =
    virtualInternshipsTestimonials?.testimonials?.nodes ?? [];
  const grantAmount = gviVIConfig?.viGrantAmount;
  const pageTitle = page?.title ?? "";

  const programCode = acfLandingPage?.programCode;

  const applyLink = `/application-form/?virtual_program&program_code=${programCode}&program_title=${pageTitle}`;

  const enquireNow = programCode
    ? `/enquiry-form/?virtual&program=virtual&program_code=${programCode}&program_title=${pageTitle}`
    : "/enquiry-form/";

  const contentGridBlockTitle =
    acfLandingPage?.masterclassTitle ?? gviVIConfig?.viMasterclassTitle ?? "";
  const contentGridBlockDescription =
    acfLandingPage?.genContentGridBlockTitle ??
    gviVIConfig?.viMasterclassText ??
    "";
  const contentGridBlocks =
    acfLandingPage?.masterclasses?.map((block) => {
      return {
        title: block?.classTitle ?? "",
        subtitle: "",
        description:
          `${block?.speakerName ? `By ${block?.speakerName}` : ""}  ${
            block?.speakerTitle ? `<br/> ${block?.speakerTitle}` : ""
          }` ?? "",
        imgUrl: block?.speakerImage?.sourceUrl ?? "",
        icon: "",
        titleClass:
          "flex-1 font-normal text-black-lighter dark:text-white text-xl",
        headerTitleContainerClass:
          "pb-4 flex flex-row items-center border-b-0 border-gray-lighter justify-between text-black-lighter dark:text-gray-generic",
        imgHeight: "h-56",
        imgClass: "object-cover object-center w-full rounded-t-md",
        cardBoxCoverClass: "bg-transparent flex flex-col h-full",
        contentWrapperClass:
          "flex flex-col p-6 rounded-b bg-white dark:gvi-dark-gray-bg flex-1",
        descriptionClass:
          "text-left text-black-lighter dark:text-gray-generic h-20"
      };
    }) ?? [];

  const globalTeamBlockTitle =
    acfLandingPage?.globalTeamTitle ?? "Meet your global team" ?? "";
  const globalTeamBlockDescription = "";
  const globalTeamBlocks =
    acfLandingPage?.globalTeamMembers?.map((block) => {
      return {
        title: block?.memberName ?? "",
        subtitle: block?.memberTitle,
        description: block?.memberDescription ?? "",
        descriptionAsComponent: (
          <CollapsibleWrapper
            desc={block?.memberDescription}
            descStyle={"text-left text-black-lighter dark:text-gray-generic"}
            visibleLength={"80"}
            visibleLines={"5"}
            btnStyle="underline underline-offset-2 pt-4 text-gray-generic dark:text-white text-sm flex gap-4 items-center"
          />
        ),
        imgUrl: block?.memberImage?.sourceUrl ?? "",
        icon: "",
        titleClass:
          "w-full font-semibold text-black-lighter dark:text-white text-xl",
        subTitleClass: "text-xl pt-2 pb-1 font-light font-body",
        subTitleMobileClass: "hidden",
        headerTitleContainerClass:
          "pb-4 flex flex-col border-b border-gray-lighter text-black-lighter dark:text-gray-generic mb-3",
        imgHeight: "h-56 sm:h-full",
        imgClass: "object-cover object-center w-full rounded-t-md",
        imgContainerClass: "relative sm:w-1/2",
        cardBoxCoverClass:
          "bg-transparent flex flex-col sm:flex-row w-full h-full",
        contentWrapperClass:
          "flex flex-col p-6 rounded-b bg-white dark:gvi-dark-gray-bg w-full",
        descriptionClass: "text-left text-black-lighter dark:text-gray-generic"
      };
    }) ?? [];

  // console.debug("debug virtual course > pageDetails", page);

  return (
    <>
      <VIHero
        heroLocalImg={isProgram ? prgmBackgroundImage : backgroundImage}
        heroContent={false}
        heroTitle={pageTitle}
        logoBoxClass={""}
        logoBoxDesc={
          "Includes courses in partnership <br /> with the University of Richmond"
        }
        applyLink={applyLink}
        enquireLink={enquireNow}
      />

      <VIIntro
        overviewContent={overviewContent}
        viOverviewTitle={viOverviewTitle}
        isProgram={isProgram}
      />
      <div className="sticky-container">
        <VIStickyBar
          ref={stickyBarRef}
          applyLink={applyLink}
          enquireNowLink={enquireNow}
        />
        {focusAreas?.length > 0 ? (
          <div ref={sliderRef}>
            <VISubjectSlider focusAreas={focusAreas} />
          </div>
        ) : (
          <div ref={sliderRef} />
        )}

        {isProgram && viPrgmProjects && viPrgmProjects?.length > 0 && (
          <VIPreviousProjects
            viPrgmProjectsTitle={viPrgmProjectsTitle}
            viPrgmProjectsDesc={viPrgmProjectsDesc}
            viPrgmProjects={viPrgmProjects}
          />
        )}

        <TestimonialVideoGallery
          genFeaturedGalleryTitle={genFeaturedGalleryTitle}
          pageTestimonials={isProgram ? viPrgmGallery : genFeaturedVideos}
        />

        <VIOptionCardList
          viWhatYouGet={viWhatYouGet}
          viWhatYouGetTitle={viWhatYouGetTitle}
        />

        <VIGrant
          fallbackBgImage={bgImage}
          promoFirstText={promoFirstText}
          promoMiddleText={promoMiddleText}
          promoLastText={promoLastText}
          title={areaTitle}
          description={areaDescription}
          grantAmount={grantAmount}
          applyLink={applyLink}
        />

        {isProgram && viPrgmActivities && viPrgmActivities?.length > 0 && (
          <VITypicalWeek
            title={viPrgmActivityTitle}
            blocks={viPrgmActivities}
          />
        )}

        {isProgram && (
          <VICurriculum
            curriculumTitle={curriculumTitle}
            curriculumDesc={curriculumDesc}
            curriculumExtras={curriculumExtras}
            curriculumList={curriculumList}
            curriculumImageDesktop={curriculumImageDesktop}
            curriculumImageMobile={curriculumImageMobile}
            applyLink={applyLink}
          />
        )}

        {isProgram && contentGridBlocks && contentGridBlocks?.length > 0 && (
          <VIMasterclass
            title={contentGridBlockTitle}
            description={contentGridBlockDescription}
            blocks={contentGridBlocks}
          />
        )}

        {isProgram && globalTeamBlocks && globalTeamBlocks?.length > 0 && (
          <VIGlobalteam
            title={globalTeamBlockTitle}
            description={globalTeamBlockDescription}
            blocks={globalTeamBlocks}
          />
        )}

        <VIPrices
          key={"vi-prices-container"}
          subCourses={subCourses}
          courses={courses}
          viTime5={viTime5}
          viTime10={viTime10}
          vi40HoursDurationPrice={vi40HoursDurationPrice}
          vi80HoursDurationPrice={vi80HoursDurationPrice}
          vi120HoursDurationPrice={vi120HoursDurationPrice}
          viDatesAndCostsTitle={viDatesAndCostsTitle}
          currencySymbol={currencySymbol}
          applyLink={applyLink}
        />
        {testimonialCollection?.length > 0 && (
          <VITestimonials testimonials={testimonialCollection} />
        )}
        {!isProgram && <VIPartnerShip />}
        {isProgram && (
          <Newsletter headerCol="sm:bg-gray-tertiary sm:dark:bg-gray-standard" />
        )}
      </div>
    </>
  );
};

export default VirtualInternshipPageComponent;
